/**
 * The root: the landing for "/" (in the first bundle, painted without waiting for the session check), the app shell for
 * every other path (its own chunk, ADR-0048), and on every page the cookie consent and the referral capture. The public
 * pages are prerendered at build time and hydrated (ADR-0061): they render for a guest at once and turn into the
 * signed-in view when the session check answers.
 */
import { lazy, Suspense, useLayoutEffect } from "react";
import { useLocation } from "wouter";
import { LandingPage } from "../features/site/Landing";
import { useLang, useShellT } from "../i18n";
import { knownDevice } from "../lib/device";
import { useHydrated } from "../lib/hydration";
import { applyPageMeta } from "../lib/pageMeta";
import { preloadable } from "../lib/preloadable";
import { useMe } from "../lib/session";
import { LogoMark } from "../ui/Logo";
import { ConsentHost } from "./ConsentHost";
import { isAppPath, isLanding } from "./publicRoutes";

const Shell = preloadable(() => import("./Shell").then((m) => m.Shell));

/** Loads the code of the page at `path` (without its language prefix) (the prerender before rendering it, the browser before hydrating it). */
export async function preloadRoute(path: string): Promise<void> {
  if (isLanding(path)) return;
  await Shell.preload();
  await (await import("./Shell")).preloadPage(path);
}
const ReferralCapture = lazy(() =>
  import("../features/money/TraderMoney").then((m) => ({
    default: function ReferralCapture({ signedIn }: { signedIn: boolean }) {
      m.useReferralCapture(signedIn);
      return null;
    },
  })),
);

/**
 * A referral code waits in the URL (?ref=) or in storage (prop.ref, written by useReferralCapture): only then is the
 * money code fetched to capture or attach it, so a plain visit does not download it.
 */
function referralPending(): boolean {
  try {
    return new URLSearchParams(location.search).has("ref") || !!localStorage.getItem("prop.ref");
  } catch {
    return false;
  }
}

function Loading() {
  const t = useShellT();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-sm text-muted-foreground">
      <LogoMark size={56} className="animate-pulse" />
      {t("loading")}
    </div>
  );
}

export function App() {
  const [location] = useLocation();
  const { lang } = useLang();
  // The site's front page is open to everyone, signed in or not; the cabinet lives at /accounts. Its largest paint is
  // the hero picture, so it does not wait for the session check: the sign-in link turns into the cabinet link then. A
  // browser that never signed in (no device id) skips the check on the landing: there is no session to find.
  const landing = isLanding(location);
  const me = useMe({ enabled: !landing || knownDevice() });
  const hydrated = useHydrated();
  // The head follows the page on client-side navigation; on a prerendered page it already holds these values.
  useLayoutEffect(() => applyPageMeta(location, lang), [location, lang]);
  return (
    <>
      {landing ? (
        <LandingPage signedIn={!!me.data} />
      ) : me.isPending && isAppPath(location) ? (
        <Loading />
      ) : (
        <Suspense fallback={<Loading />}>
          <Shell user={me.data ?? null} />
        </Suspense>
      )}
      <ConsentHost />
      {hydrated && (!me.isPending || me.fetchStatus === "idle") && referralPending() && (
        <Suspense fallback={null}>
          <ReferralCapture signedIn={!!me.data} />
        </Suspense>
      )}
    </>
  );
}
